<template>
<v-dialog
    v-model="dialog"
    fullscreen
    v-if="value != null"
>
    <v-card>
        <v-toolbar
            dark
            color="primary"
        >
            <v-toolbar-title>Edit Contact Card</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-toolbar-items>
                <v-btn
                    dark
                    text
                    @click="onClickDiscardChanges"
                >
                    Cancel
                </v-btn>

                <v-btn
                    dark
                    text
                    @click="onClickSave"
                >
                    Save &amp; Close
                </v-btn>
            </v-toolbar-items>
        </v-toolbar>

        <v-card-text
            style="padding-top: 15px"
        >
            <v-text-field
                label="Internal Label"
                v-model="value.internal_label"
                hint="This is not visible to customers"
                autocomplete="off"
            />

            <v-select
                label="Type"
                v-model="value.kind"
                :items="$rc.enumChoices('red_chirp_vcard_kind',false)"
            />

            <m-text-field
                label="Default text to send with contact card"
                v-model="value.default_text_to_send_with"
                emoji
            />

            <div>
                <b>Logo</b>
            </div>

            <m-edit-blobs
                label="Logo"
                v-model="value.photo_blob_ids"
                :process_media="true"
                :externally_visible="true"
                :limit_to_size="229376"
                :accept="['png','jpg','jpeg']"
                empty_text="No logo"
            />

            <div
                style="margin-bottom: 25px"
            >
                <b>Logo Warnings</b> 
                <ul>
                    <li>Files are limited to <b>224kb</b></li>
                    <li>To display properly on iPhone / iOS, <b>your logo should be square</b></li>
                    <li>To display properly on iPhone / iOS, <b>your logo needs to look good when cropped to a circle</b> (a.k.a. be an avatar style logo)</li> 
                    <li>We <b>strongly</b> suggest you have lots of negative space around your logo.  Test it out using <a href="https://avatarcropper.com/" target="_blank">Avatar Cropper</a></li>
                    <li>Display of logos is <b>very inconsistent</b> from device to device (iOS vs Android etc.)</li>
                    <li>Display of logos is <b>very inconsistent</b> from device version to device and software version (e.g. iPhone 12 vs 13, Google Pixel 4 vs 5, etc.)</li>
                </ul>
            </div>
            

            <v-row
                v-if="value.kind == 'individual'"
            >
                <v-col>
                    <v-text-field
                        label="First Name"
                        v-model="value.first_name"
                        autocomplete="off"
                    />
                </v-col>

                <v-col>
                    <v-text-field
                        label="Last Name"
                        v-model="value.last_name"
                        autocomplete="off"
                    />
                </v-col>
            </v-row>

            <v-text-field
                label="Organization Name"
                v-model="value.organization_names[0]"
                autocomplete="off"
            />

            <b>Phone Numbers</b>

            <v-row
                v-for="(item, index) in value.phone_numbers"
                :key="'phone_'+index"
            >
                <v-col>
                    <v-text-field
                        label="Phone Number"
                        v-model="value.phone_numbers[index].phone_number"
                        :readonly="!canEditPhoneNumber(item)"
                        :disabled="!canEditPhoneNumber(item)"
                        autocomplete="off"
                    />
                </v-col>
                
                <v-col>
                    <v-select
                        label="Type"
                        v-model="value.phone_numbers[index].type"
                        :items="$rc.enumChoices('red_chirp_vcard_telephone_type',false)"
                    />
                </v-col>

                
                <v-col
                    style="max-width: 35px;"
                    v-if="canEditPhoneNumber(item)"
                >
                    <v-menu 
                        bottom 
                        offset-y  
                    >
                    
                    <template v-slot:activator="{ on }">
                            
                        <v-icon 
                            v-on="on"
                            style="margin-right: 10px; margin-top: 15px;"
                        >
                            mdi-dots-vertical
                        </v-icon>
                            
                    </template>
                    
                    <v-list>
                        <v-list-item
                            v-on:click="onClickDeletePhoneNumber(index)"

                        >
                            <v-list-item-title>Delete...</v-list-item-title>
                        </v-list-item>
                    
                    </v-list>
                    
                    </v-menu> 
                </v-col>
            </v-row>

            <m-icon-bar
                v-on:add="onClickAddPhoneNumber"
            />

            <b>Email Addresses</b>

            <v-row
                v-for="(item, index) in value.emails"
                :key="'email_'+index"
            >
                <v-col>
                    <v-text-field
                        label="Email Address"
                        v-model="value.emails[index].email_address"
                        autocomplete="off"
                    />
                </v-col>
                
                <v-col>
                    <v-select
                        label="Type"
                        v-model="value.emails[index].type"
                        :items="$rc.enumChoices('red_chirp_vcard_email_type',false)"
                    />
                </v-col>

                <v-col
                    style="max-width: 35px;"
                >
                    <v-menu 
                        bottom 
                        offset-y  
                    >
                    
                    <template v-slot:activator="{ on }">
                            
                        <v-icon 
                            v-on="on"
                            style="margin-right: 10px; margin-top: 15px;"
                        >
                            mdi-dots-vertical
                        </v-icon>
                            
                    </template>
                    
                    <v-list>
                        <v-list-item
                            v-on:click="onClickDeleteEmail(index)"

                        >
                            <v-list-item-title>Delete...</v-list-item-title>
                        </v-list-item>
                    
                    </v-list>
                    
                    </v-menu> 
                </v-col>
            </v-row>

            <m-icon-bar
                v-on:add="onClickAddEmail"
            />


            <div
                 v-for="(item, index) in value.addresses"
                :key="'addr_'+index"
            >
                <b>Physical Addresses</b>

                <v-row>
                    <v-col>
                        <v-select
                            label="Type"
                            v-model="value.addresses[index].type"
                            :items="$rc.enumChoices('red_chirp_vcard_address_type',false)"
                        />
                    </v-col>

                    <v-col
                        style="max-width: 35px;"
                    >
                        <v-menu 
                            bottom 
                            offset-y  
                        >
                        
                        <template v-slot:activator="{ on }">
                                
                            <v-icon 
                                v-on="on"
                                style="margin-right: 10px; margin-top: 15px;"
                            >
                                mdi-dots-vertical
                            </v-icon>
                                
                        </template>
                        
                        <v-list>
                            <v-list-item
                                v-on:click="onClickDeletePhysicalAddress(index)"

                            >
                                <v-list-item-title>Delete...</v-list-item-title>
                            </v-list-item>
                        
                        </v-list>
                        
                        </v-menu> 
                    </v-col>
                </v-row>

                <v-text-field
                    label="Address"
                    v-model="value.addresses[index].address_one"
                    autocomplete="off"
                />

                <v-text-field
                    label="Address (line two)"
                    v-model="value.addresses[index].address_two"
                    autocomplete="off"
                />

                <v-row>
                    <v-col>
                        <v-text-field
                            label="City"
                            v-model="value.addresses[index].city"
                            autocomplete="off"
                        />
                    </v-col>

                    <v-col>
                        <v-text-field
                            label="State / Provence"
                            v-model="value.addresses[index].state"
                            autocomplete="off"
                        />
                    </v-col>

                    <v-col>
                        <v-text-field
                            label="Zip / Postal Code"
                            v-model="value.addresses[index].zip"
                            autocomplete="off"
                        />
                    </v-col>
                </v-row>

                <v-text-field
                    label="Country"
                    v-model="value.addresses[index].country"
                    autocomplete="off"
                />

            </div>

             <m-icon-bar
                v-on:add="onClickAddPhysicalAddress"
            />

        </v-card-text>

    </v-card>
</v-dialog>
</template>

<script>

const _ = require('lodash');
const E164 = require('@/js/E164.js');

export default {

    data() {
        return {
            dialog: false,
            is_valid: true,
            value: null,
        }
    },

    methods: {

        showDialog(value)
        {
            this.value = _.cloneDeep(value);

            this.dialog = true;
            this.$router.dialog_guard = this.dialogGuard;
        },

        closeDialog()
        {
            this.dialog = false;
            this.$router.dialog_guard = null;
        },

        dialogGuard(to, from, next)
        {
            if ( this.dialog == false)
            {
                // slipup somehow
                this.$router.dialog_guard = null;
                return;
            }
        
            this.save();
            this.dialog = false;
            next(false);
        },

        canEditPhoneNumber(item)
        {
            if ( this.value == null || item == null || item.phone_number == null || this.value.for_organization_owned_endpoint == null ) 
                return true;
            
            let parsed = E164.createEndpointFromUSPhoneNumber(item.phone_number);

            if ( parsed == null ) return true;

            if ( parsed != this.value.for_organization_owned_endpoint ) return true;

            // If here, then the parsed phone number is the oranization owned endpoint (the only way it is editable is if it is present more than once)

            let with_ooe = this.value.phone_numbers
                .map((el) => el.phone_number)
                .map((el) => E164.createEndpointFromUSPhoneNumber(el))
                .filter((el) => el == this.value.for_organization_owned_endpoint );

            if ( with_ooe.length > 1 )
                return true;

            return false;
        },

        onClickSave()
        {
            this.save();
            this.closeDialog();
        },

        onClickDiscardChanges()
        {
            this.closeDialog();
        },

        save()
        {
            this.$rc.showSpinner();
            
            this.$rc.rest(
                'PUT: /api/v1/vcard',
            
                this.value,
            
                (data) => {
                    this.$rc.hideSpinner();

                    this.$emit('input', this.value);
                },
            
                () => {
                    this.$rc.hideSpinner();
                    this.$rc.alert('Error','Unable to complete API call');
                },
            );
        },

        onClickAddPhoneNumber()
        {
            this.value.phone_numbers.push(
                {'phone_number': '', type: 'work'},
            );
        },

        onClickDeletePhoneNumber(index)
        {
            this.value.phone_numbers.splice(index,1);  
        },

        onClickDeleteEmail(index)
        {
            this.value.emails.splice(index,1);  
        },

        onClickAddEmail()
        {
            this.value.emails.push(
                {'email_address': '', type: 'work'},
            );
        },

        onClickAddPhysicalAddress()
        {
            this.value.addresses.push(
                { 'type': 'work' }
            );
        },

        onClickDeletePhysicalAddress(index)
        {
            this.value.addresses.splice(index,1);  
        },

    },
}
</script>

<style lang="scss" scoped>
</style>